import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import {
  getUserProfile,
  updateUserProfileFields
} from "../../../service/userService";
import "./ProfileEdit.css";

const positions = [
  "Goleiro",
  "Zagueiro",
  "Lateral",
  "Volante",
  "Meia",
  "Atacante",
  "Pivô",
  "Fixo",
  "Ala"
];

const toDateInputValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().split("T")[0];
};

const ProfileEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nickname: "",
    birth: "",
    altura: "",
    peso: "",
    posicao: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getUserProfile(user.uid);
        setFormData({
          nickname: data?.nickname || "",
          birth: toDateInputValue(data?.birth),
          altura: data?.altura || "",
          peso: data?.peso || "",
          posicao: data?.posicao || ""
        });
      } catch (profileError) {
        console.error("Erro ao carregar dados do perfil", profileError);
        setError("Não foi possível carregar seus dados agora.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const disableSubmit = useMemo(() => {
    return saving || !formData.nickname.trim();
  }, [formData.nickname, saving]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateUserProfileFields(user.uid, {
        nickname: formData.nickname.trim(),
        birth: formData.birth,
        altura: formData.altura,
        peso: formData.peso,
        posicao: formData.posicao
      });
      setSuccess("Perfil atualizado!");
    } catch (updateError) {
      console.error("Erro ao atualizar perfil", updateError);
      setError("Não foi possível salvar as alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/perfil");
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card profile-edit-card">
            <div className="topo-page">
              <strong># Editar perfil</strong>
            </div>
            <p className="profile-edit-description">
              Atualize os dados que são exibidos nas turmas e partidas.
            </p>
            {loading ? (
              <div className="profile-edit-loading">Carregando...</div>
            ) : (
              <form className="profile-edit-form" onSubmit={handleSubmit}>
                {error.trim() && (
                  <p className="profile-edit-error" role="alert">
                    {error}
                  </p>
                )}
                {success && <p className="profile-edit-success">{success}</p>}
                <label htmlFor="nickname">Nickname</label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  placeholder="Seu apelido"
                  value={formData.nickname}
                  onChange={handleChange}
                  required
                />

                <label htmlFor="birth">Data de nascimento</label>
                <input
                  id="birth"
                  name="birth"
                  type="date"
                  value={formData.birth}
                  onChange={handleChange}
                />

                <label htmlFor="altura">Altura (em cm)</label>
                <input
                  id="altura"
                  name="altura"
                  type="number"
                  min="0"
                  placeholder="Ex: 178"
                  value={formData.altura}
                  onChange={handleChange}
                />

                <label htmlFor="peso">Peso (em kg)</label>
                <input
                  id="peso"
                  name="peso"
                  type="number"
                  min="0"
                  placeholder="Ex: 75"
                  value={formData.peso}
                  onChange={handleChange}
                />

                <label htmlFor="posicao">Posição preferida</label>
                <select
                  id="posicao"
                  name="posicao"
                  value={formData.posicao}
                  onChange={handleChange}
                >
                  <option value="">Selecione</option>
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>

                <div className="profile-edit-actions">
                  <button type="button" className="btn-outline" onClick={handleBack}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-default" disabled={disableSubmit}>
                    {saving ? "Salvando..." : "Salvar alterações"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProfileEdit;
